/**
 * Redirect map for legacy `/problems/topics/<slug>` URLs (LG-339, LG-55 PR 6).
 *
 * Before the taxonomy migration, topic browse resolved a URL slug against
 * `slugify(Problem.tags)`. The 29 live legacy slugs are the distinct
 * `slugify(tag)` values over published/public BCTCI problems. After the
 * migration, browse resolves against `TopicDefinition.slug` /
 * `SubtopicDefinition.slug` (URL-safe forms from LG-310).
 *
 * 12 legacy slugs already equal a topic slug and need no redirect
 * (backtracking, binary-search, bit-manipulation, dynamic-arrays,
 * dynamic-programming, greedy, math, recursion, sorting, topological-sort,
 * two-pointers, union-find). The remaining 17 are mapped here.
 *
 * The mapping was derived from — and every target verified against — the dev DB
 * on 2026-07-28, not the LG-339 brief's table, which predates the LG-310 slug
 * normalization and miscounts the plural renames (stack→stacks, string→
 * string-manipulation, linked-list→linked-lists, trie→tries) as clean matches.
 *
 * Keys are legacy topic slugs; values are absolute target paths. Only consulted
 * for the `/problems/topics/<slug>` route, so a key that collides with a real
 * subtopic slug is harmless.
 */
export const LEGACY_TOPIC_REDIRECTS: Readonly<Record<string, string>> = {
  // Direct topic renames — pluralized/normalized names (LG-310).
  'sliding-window': '/problems/topics/sliding-windows',
  graph: '/problems/topics/graphs',
  tree: '/problems/topics/trees',
  queue: '/problems/topics/queues',
  matrix: '/problems/topics/grids-and-matrices',
  'heap-priority-queue': '/problems/topics/heaps',
  'linked-list': '/problems/topics/linked-lists',
  stack: '/problems/topics/stacks',
  string: '/problems/topics/string-manipulation',
  trie: '/problems/topics/tries',

  // Legacy topics that are subtopics in the taxonomy.
  'monotonic-stack': '/problems/subtopics/stacks-monotonic-stacks',
  'prefix-sum': '/problems/subtopics/prefix-sum',
  // Its sole problem (diameter-of-binary-tree) lives in Trees / DFS.
  'depth-first-search': '/problems/subtopics/trees-dfs',

  // Broad legacy concepts with no single subtopic → nearest umbrella topic.
  'binary-tree': '/problems/topics/trees',
  'hash-table': '/problems/topics/maps',

  // The Sets & Maps chapter spans both topics; the combined view survives as the
  // chapter (decision of record #8). Target chapter30 — the currently-served
  // chapter URL — until LG-311 makes `sets-maps` canonical and forwards it.
  'sets-and-maps': '/problems/book/chapter30',

  // Generic 'Array' tag: its two problems live in different topics
  // (two-pointers, sets), so send to the full browse list.
  array: '/problems',
}

/**
 * Resolve a legacy topic slug to its taxonomy target path, or `null` if the
 * slug is not a legacy one (i.e. it should be resolved as a current topic).
 */
export function resolveLegacyTopicRedirect(slug: string): string | null {
  return LEGACY_TOPIC_REDIRECTS[slug] ?? null
}
